/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import React, { useState, useEffect, Suspense } from "react";
import { FiPlus, FiFlag, FiMenu } from "react-icons/fi";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuthStore } from "@/store/useAuthStore";
import ThreadCard from "./ThreadCard";
import Sidebar from "./Sidebar";
import CreateThreadModal from "./CreateThreadModal";
import axios from "axios";
import Image from "next/image";
import {
  LuMessagesSquare,
  LuBookOpen,
  LuPlane,
  LuDollarSign,
  LuBriefcaseBusiness,
  LuUsers,
  LuGlobe,
  LuX,
} from "react-icons/lu";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:3001";

type ViewMode = "all" | "saved" | "mine";

const CATEGORIES = [
  { value: "all", label: "All Discussions" },
  { value: "study-academics", label: "Study & Academics" },
  { value: "visa-immigration", label: "Visa & Immigration" },
  { value: "travel-living", label: "Travel & Living" },
  { value: "finance-scholarships", label: "Finance & Scholarships" },
  { value: "career-jobs", label: "Career & Jobs" },
  { value: "social-events", label: "Social & Events" },
];

type Comment = {
  id: string;
  user_id: string;
  user_name: string;
  user_image?: string;
  comment: string;
  status?: string; // 'pending' | 'approved' | 'rejected'
  rejectionReason?: string | null;
  createdAt: string;
  updatedAt: string;
};

type ThreadPost = {
  id: string;
  userId: string;
  title: string | null;
  post: string;
  isSeeded?: boolean;
  seededAuthorName?: string | null;
  category: string;
  likes: string[];
  dislikes: string[];
  likeCount: number;
  dislikeCount: number;
  commentCount: number;
  userLiked: boolean;
  userDisliked: boolean;
  userSaved: boolean;
  upvoteCount: number;
  userVote: -1 | 0 | 1;
  comments: Comment[];
  status?: "PENDING" | "APPROVED" | "REJECTED";
  rejectionReason?: string | null;
  createdAt: string;
  updatedAt: string;
  author?: {
    id: string;
    name: string;
    postCount: number;
  };
};

type ForumSummary = {
  totalPosts: number;
  activeUsers: number;
  onlineNow: number;
  categories: Array<{
    key: string;
    label: string;
    count: number;
  }>;
};

function ForumContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, id: userId, loading, accessToken } = useAuthStore();

  // Get active category from URL query parameter
  const activeCategory = searchParams.get("tab") || "all";
  const [view, setView] = useState<ViewMode>("all");
  // #238 free-text search over thread title/body (applies to the "all" feed).
  const [searchTerm, setSearchTerm] = useState("");
  // Real-time channel suggestions: if the search text matches a category
  // label (e.g. "visa" -> "Visa & Immigration"), offer it as a shortcut to
  // jump straight to that channel instead of a text search.
  const [showChannelSuggestions, setShowChannelSuggestions] = useState(false);

  const [threads, setThreads] = useState<ThreadPost[]>([]);
  const [savedCount, setSavedCount] = useState(0);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [feedError, setFeedError] = useState(false);
  const [summary, setSummary] = useState<ForumSummary | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [openComments, setOpenComments] = useState<Set<string>>(new Set());

  // New thread modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Mobile sidebar drawer
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Inline reply per thread
  const [replyOpenId, setReplyOpenId] = useState<string | null>(null);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});

  // Edit a thread inline
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [threadEditTitle, setThreadEditTitle] = useState("");
  const [threadEditPost, setThreadEditPost] = useState("");
  const [threadEditCategory, setThreadEditCategory] = useState("");

  // Edit a comment
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [commentEditText, setCommentEditText] = useState("");

  // Loading states
  const [creatingThread, setCreatingThread] = useState(false);
  const [updatingThread, setUpdatingThread] = useState(false);
  const [deletingThread, setDeletingThread] = useState<string | null>(null);
  const [addingComment, setAddingComment] = useState<string | null>(null);
  const [updatingComment, setUpdatingComment] = useState(false);
  const [deletingComment, setDeletingComment] = useState<string | null>(null);
  const [togglingLike, setTogglingLike] = useState<string | null>(null);
  const [togglingSave, setTogglingSave] = useState<string | null>(null);
  const [votingThread, setVotingThread] = useState<{ id: string; value: 1 | -1 } | null>(null);

  // Toast message
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  // Report modal
  const [reportTarget, setReportTarget] = useState<{
    type: "THREAD" | "COMMENT";
    targetId: string;
    postId?: string;
  } | null>(null);
  const [reportReason, setReportReason] = useState("");
  const [submittingReport, setSubmittingReport] = useState(false);

  // --- Load forum summary (counts + stats) ---
  async function loadSummary() {
    try {
      if (!accessToken) return;

      const response = await axios.get(`${API_BASE_URL}/forum/summary`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.data.success) {
        setSummary({
          totalPosts: response.data.totalPosts,
          activeUsers: response.data.activeUsers,
          onlineNow: response.data.onlineNow,
          categories: response.data.categories,
        });
      }
    } catch (error) {
      console.error("Failed to load summary:", error);
    }
  }

  // --- Load saved count (lightweight, page=1&limit=1 to get total only) ---
  async function loadSavedCount() {
    try {
      if (!accessToken) return;

      const response = await axios.get(
        `${API_BASE_URL}/forum/savedThreads?page=1&limit=1`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      if (response.data.success) {
        setSavedCount(response.data.pagination.total);
      }
    } catch (error) {
      console.error("Failed to load saved count:", error);
    }
  }

  // --- Load threads from backend ---
  async function loadThreads(
    nextView: ViewMode = view,
    category: string = activeCategory,
    page: number = 1,
    search: string = searchTerm,
  ) {
    setLoadingFeed(true);
    setFeedError(false);
    try {
      if (!accessToken) return;

      let response;
      if (nextView === "mine") {
        response = await axios.get(
          `${API_BASE_URL}/forum/myPost?page=${page}&limit=15`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (response.data.success) {
          setThreads(response.data.posts);
          setTotalPages(response.data.pagination?.totalPages ?? 1);
        }
      } else if (nextView === "saved") {
        response = await axios.get(
          `${API_BASE_URL}/forum/savedThreads?page=${page}&limit=15`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (response.data.success) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setThreads(response.data.savedThreads.map((st: any) => st.post));
          setSavedCount(response.data.pagination?.total ?? 0);
          setTotalPages(response.data.pagination?.totalPages ?? 1);
        }
        return;
      } else {
        const params = new URLSearchParams();
        if (category && category !== "all") {
          params.append("category", category);
        }
        const q = search?.trim();
        if (q) {
          params.append("search", q);
        }
        params.append("page", String(page));
        params.append("limit", "15");

        response = await axios.get(
          `${API_BASE_URL}/forum/threads?${params.toString()}`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (response.data.success) {
          setThreads(response.data.posts);
          setTotalPages(response.data.pagination?.totalPages ?? 1);
        }
      }

      // Always refresh saved count badge when loading the "all" or "mine" feed
      await loadSavedCount();
    } catch (error) {
      console.error("Failed to load threads:", error);
      setFeedError(true);
    } finally {
      setLoadingFeed(false);
    }
  }

  // Navigate to a specific page
  function goToPage(page: number) {
    setCurrentPage(page);
    loadThreads(view, activeCategory, page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Single source of feed loading: react to accessToken, tab (view) and category.
  // Previously two effects both fired on mount → duplicate racing requests.
  // Must key off accessToken (not user): `user` is rehydrated from
  // localStorage synchronously on mount while accessToken is still null
  // (excluded from persistence, fetched async via fetchAuthSession). Keying
  // on `user` fired this effect once with no token yet, loadThreads bailed
  // silently on `if (!accessToken) return`, and since `user` never changes
  // again the feed never loaded.
  useEffect(() => {
    if (accessToken) {
      setCurrentPage(1);
      setTotalPages(1);
      loadThreads(view, activeCategory, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, view, activeCategory]);

  useEffect(() => {
    if (accessToken) loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, activeCategory]);

  // Close mobile drawer when category changes (link navigation)
  useEffect(() => {
    setSidebarOpen(false);
  }, [activeCategory]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  // --- Thread actions ---
  // Create thread with title and body (new API)
  async function createThreadWithModal(data: {
    title: string;
    body: string | null;
    category: string;
  }) {
    setCreatingThread(true);
    try {
      if (!accessToken) throw new Error("Not authenticated");

      await axios.post(`${API_BASE_URL}/forum/threads`, data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // Close modal and refresh
      setShowCreateModal(false);
      showToast("Thread submitted! Pending moderator approval.", "info");
      await loadThreads(view, activeCategory, currentPage);
      await loadSummary(); // Refresh counts after creating a post
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        showToast(
          error.response.data?.message || "You cannot create posts.",
          "error",
        );
        setShowCreateModal(false);
        return;
      }
      console.error("Failed to create thread:", error);
      throw error;
    } finally {
      setCreatingThread(false);
    }
  }

  async function updateThread(
    id: string,
    title: string,
    post: string,
    category: string,
  ): Promise<boolean> {
    setUpdatingThread(true);
    try {
      if (!accessToken) return false;

      await axios.patch(
        `${API_BASE_URL}/forum/threads/${id}`,
        { title, body: post || null, category },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      await loadThreads(view, activeCategory, currentPage);
      await loadSummary(); // Refresh counts if category changed
      return true;
    } catch (error) {
      console.error("Failed to update thread:", error);
      showToast("Failed to save changes. Please try again.", "error");
      return false;
    } finally {
      setUpdatingThread(false);
    }
  }

  async function deleteThread(id: string) {
    if (!window.confirm("Delete this thread? This cannot be undone.")) return;
    setDeletingThread(id);
    try {
      if (!accessToken) return;

      await axios.delete(`${API_BASE_URL}/forum/deletePost?id=${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      await loadThreads(view, activeCategory, currentPage);
      await loadSummary(); // Refresh counts after deleting a post
    } catch (error) {
      console.error("Failed to delete thread:", error);
      showToast("Failed to delete thread. Please try again.", "error");
    } finally {
      setDeletingThread(null);
    }
  }

  async function toggleSave(id: string, nextSaved: boolean) {
    setTogglingSave(id);
    try {
      if (!accessToken) return;

      if (nextSaved) {
        // Next state is saved, so we need to save it
        await axios.post(
          `${API_BASE_URL}/forum/saveThread`,
          { postId: id },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
      } else {
        // Next state is unsaved, so we need to unsave it
        await axios.delete(`${API_BASE_URL}/forum/unsaveThread`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          data: { postId: id },
        });
      }
      await loadThreads(view, activeCategory, currentPage);
    } catch (error) {
      console.error("Failed to toggle save:", error);
      showToast("Failed to update saved posts. Please try again.", "error");
    } finally {
      setTogglingSave(null);
    }
  }

  async function voteThread(id: string, value: -1 | 1) {
    if (!accessToken) return;
    setVotingThread({ id, value });

    // Optimistic UI update
    const originalThreads = [...threads];
    setThreads((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;

        // Calculate new vote state (Reddit-style toggle)
        let newUserVote: -1 | 0 | 1 = value;
        if (t.userVote === value) {
          // Clicking same vote removes it
          newUserVote = 0;
        }

        // Calculate new upvote count
        let newUpvoteCount = t.upvoteCount;
        if (t.userVote === 1) {
          // Remove previous upvote
          newUpvoteCount--;
        }
        if (newUserVote === 1) {
          // Add new upvote
          newUpvoteCount++;
        }

        return {
          ...t,
          userVote: newUserVote,
          upvoteCount: newUpvoteCount,
        };
      }),
    );

    try {
      if (!accessToken) return;

      await axios.post(
        `${API_BASE_URL}/forum/threads/${id}/vote`,
        { value },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      // Optimistic update already applied — no full reload needed
    } catch (error) {
      console.error("Failed to vote:", error);
      // Rollback on error
      setThreads(originalThreads);
    } finally {
      setVotingThread(null);
    }
  }

  async function giveLike(id: string) {
    setTogglingLike(id);
    // Optimistic update
    const original = [...threads];
    setThreads((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        const toggled = !t.userLiked;
        return {
          ...t,
          userLiked: toggled,
          likeCount: toggled ? t.likeCount + 1 : t.likeCount - 1,
          userDisliked: false,
          dislikeCount: t.userDisliked ? t.dislikeCount - 1 : t.dislikeCount,
        };
      }),
    );
    try {
      if (!accessToken) return;
      await axios.post(
        `${API_BASE_URL}/forum/giveLike`,
        { postId: id },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
    } catch (error) {
      console.error("Failed to like:", error);
      setThreads(original);
    } finally {
      setTogglingLike(null);
    }
  }

  async function giveDislike(id: string) {
    setTogglingLike(id);
    // Optimistic update
    const original = [...threads];
    setThreads((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        const toggled = !t.userDisliked;
        return {
          ...t,
          userDisliked: toggled,
          dislikeCount: toggled ? t.dislikeCount + 1 : t.dislikeCount - 1,
          userLiked: false,
          likeCount: t.userLiked ? t.likeCount - 1 : t.likeCount,
        };
      }),
    );
    try {
      if (!accessToken) return;
      await axios.post(
        `${API_BASE_URL}/forum/giveDislike`,
        { postId: id },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
    } catch (error) {
      console.error("Failed to dislike:", error);
      setThreads(original);
    } finally {
      setTogglingLike(null);
    }
  }

  // --- Comments ---
  async function addComment(
    threadId: string,
    comment: string,
  ): Promise<boolean> {
    setAddingComment(threadId);
    try {
      if (!accessToken) return false;

      await axios.post(
        `${API_BASE_URL}/forum/addComment`,
        { postId: threadId, comment },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      showToast("Comment submitted. Pending moderator approval.", "info");
      await loadThreads(view, activeCategory, currentPage);
      return true;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        showToast(
          error.response.data?.message || "You cannot add comments.",
          "error",
        );
      } else {
        console.error("Failed to add comment:", error);
        showToast("Failed to post comment. Please try again.", "error");
      }
      return false;
    } finally {
      setAddingComment(null);
    }
  }

  async function updateComment(
    postId: string,
    commentId: string,
    comment: string,
  ): Promise<boolean> {
    setUpdatingComment(true);
    try {
      if (!accessToken) return false;

      await axios.patch(
        `${API_BASE_URL}/forum/editComment`,
        { postId, commentId, comment },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      await loadThreads(view, activeCategory, currentPage);
      return true;
    } catch (error) {
      console.error("Failed to update comment:", error);
      showToast("Failed to save comment. Please try again.", "error");
      return false;
    } finally {
      setUpdatingComment(false);
    }
  }

  async function deleteComment(postId: string, commentId: string) {
    if (!window.confirm("Delete this comment? This cannot be undone.")) return;
    setDeletingComment(commentId);
    try {
      if (!accessToken) return;

      await axios.delete(`${API_BASE_URL}/forum/deleteComment`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        data: { postId, commentId },
      });
      await loadThreads(view, activeCategory, currentPage);
    } catch (error) {
      console.error("Failed to delete comment:", error);
      showToast("Failed to delete comment. Please try again.", "error");
    } finally {
      setDeletingComment(null);
    }
  }

  // --- UI handlers ---
  function handlePlusClick() {
    setShowCreateModal(true);
  }

  // Channels whose label matches the in-progress search text, offered as a
  // real-time dropdown under the search box.
  const channelSuggestions = (() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return CATEGORIES.filter(
      (cat) => cat.value !== "all" && cat.label.toLowerCase().includes(q),
    );
  })();

  // Selecting a suggested channel behaves exactly like clicking it in the
  // sidebar (Sidebar.tsx uses the same `/forum` / `/forum?tab=<value>` links) —
  // it's a navigation to that channel, not a text search.
  function selectChannelSuggestion(value: string) {
    setSearchTerm("");
    setShowChannelSuggestions(false);
    router.push(value === "all" ? "/forum" : `/forum?tab=${value}`);
  }

  function startThreadEdit(id: string) {
    const th = threads.find((t) => t.id === id);
    if (!th) return;
    setEditingThreadId(id);
    setThreadEditTitle(th.title || "");
    setThreadEditPost(th.post);
    setThreadEditCategory(th.category);
  }

  function saveThreadEdit() {
    if (!editingThreadId) return;
    updateThread(
      editingThreadId,
      threadEditTitle.trim(),
      threadEditPost.trim(),
      threadEditCategory.trim(),
    ).then((ok) => {
      // Keep the editor open on failure so the user's edits aren't discarded.
      if (ok) cancelThreadEdit();
    });
  }

  function cancelThreadEdit() {
    setEditingThreadId(null);
    setThreadEditTitle("");
    setThreadEditPost("");
    setThreadEditCategory("");
  }

  function toggleCommentsUI(threadId: string) {
    setOpenComments((prev) => {
      const copy = new Set(prev);
      if (copy.has(threadId)) copy.delete(threadId);
      else copy.add(threadId);
      return copy;
    });
  }

  function openReplyInline(threadId: string) {
    setReplyOpenId((cur) => (cur === threadId ? null : threadId));
    setReplyDrafts((d) => ({ ...d, [threadId]: d[threadId] ?? "" }));
  }

  function submitReplyInline(threadId: string) {
    // Guard the keyboard shortcut (⌘/Ctrl+Enter) which bypasses the disabled
    // Post button and could otherwise double-submit.
    if (addingComment) return;
    const text = (replyDrafts[threadId] || "").trim();
    if (!text) return;
    addComment(threadId, text).then((ok) => {
      // Only clear the draft on success so a failed post doesn't lose the text.
      if (!ok) return;
      setReplyDrafts((d) => ({ ...d, [threadId]: "" }));
      setReplyOpenId(null);
    });
  }

  // Show toast with auto-dismiss
  function showToast(
    message: string,
    type: "success" | "error" | "info" = "success",
  ) {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }

  // Report content
  async function submitReport() {
    if (!reportTarget || !accessToken) return;
    setSubmittingReport(true);
    try {
      await axios.post(
        `${API_BASE_URL}/forum/report`,
        {
          targetType: reportTarget.type,
          targetId: reportTarget.targetId,
          postId: reportTarget.postId || undefined,
          reason: reportReason.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      showToast("Report submitted successfully");
      setReportTarget(null);
      setReportReason("");
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        showToast("You have already reported this content", "info");
      } else if (axios.isAxiosError(error) && error.response?.status === 400) {
        showToast(
          error.response.data?.message || "Cannot report this content",
          "error",
        );
      } else {
        showToast("Failed to submit report", "error");
      }
      setReportTarget(null);
      setReportReason("");
    } finally {
      setSubmittingReport(false);
    }
  }

  function startCommentEdit(commentId: string, currentText: string) {
    setEditingCommentId(commentId);
    setCommentEditText(currentText);
  }

  function saveCommentEdit(postId: string) {
    if (!editingCommentId) return;
    updateComment(postId, editingCommentId, commentEditText.trim()).then(
      (ok) => {
        // Keep the editor open on failure so the edit isn't silently lost.
        if (ok) cancelCommentEdit();
      },
    );
  }

  function cancelCommentEdit() {
    setEditingCommentId(null);
    setCommentEditText("");
  }

  useEffect(() => {
    if (!loading && !user) router.push("/login");
  }, [user, loading, router]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const iconMap: Record<string, any> = {
    all: LuMessagesSquare,
    "study-academics": LuBookOpen,
    "visa-immigration": LuGlobe,
    "travel-living": LuPlane,
    "finance-scholarships": LuDollarSign,
    "career-jobs": LuBriefcaseBusiness,
    "social-events": LuUsers,
  };
  const sidebarChannels = summary?.categories.map((cat) => ({
    label: cat.label,
    value: cat.key,
    href: cat.key === "all" ? "/forum" : `/forum?tab=${cat.key}`,
    count: cat.count,
    icon: iconMap[cat.key],
  }));
  const sidebarStats = {
    totalPosts: summary?.totalPosts,
    activeUsers: summary?.activeUsers,
    onlineNow: summary?.onlineNow,
  };

  return (
    <div className="min-h-dvh">
      <div className="w-[92%] mx-auto">
        {/* Mobile top bar: categories drawer toggle + view mode tabs */}
        <div className="lg:hidden flex items-center gap-2 py-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/10 transition shrink-0"
            aria-label="Open categories"
          >
            <FiMenu className="h-4 w-4" />
            <span>Categories</span>
          </button>
          <div className="flex gap-1.5 overflow-x-auto">
            <button
              onClick={() => setView("all")}
              className={`px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition ${
                view === "all"
                  ? "bg-gray-900 dark:bg-white/90 text-white dark:text-black"
                  : "bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:bg-gray-200 dark:hover:bg-white/10"
              }`}
            >
              Home
            </button>
            <button
              onClick={() => setView("mine")}
              className={`px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition ${
                view === "mine"
                  ? "bg-gray-900 dark:bg-white/90 text-white dark:text-black"
                  : "bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:bg-gray-200 dark:hover:bg-white/10"
              }`}
            >
              Your Threads
            </button>
            <button
              onClick={() => setView("saved")}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition ${
                view === "saved"
                  ? "bg-gray-900 dark:bg-white/90 text-white dark:text-black"
                  : "bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:bg-gray-200 dark:hover:bg-white/10"
              }`}
            >
              Saved
              {savedCount > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-4.5 h-4.5 px-1 rounded text-xs font-semibold ${
                    view === "saved"
                      ? "bg-white/20 dark:bg-black/20"
                      : "bg-gray-300 dark:bg-white/20"
                  }`}
                >
                  {savedCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile sidebar drawer — always rendered, transitions via translate */}
        <div
          className={`fixed inset-0 z-50 lg:hidden transition-all duration-300 ${
            sidebarOpen ? "pointer-events-auto" : "pointer-events-none"
          }`}
        >
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
              sidebarOpen ? "opacity-100" : "opacity-0"
            }`}
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer panel — slides in from left */}
          <div
            className={`absolute left-0 top-0 h-full w-72 flex flex-col bg-gray-50 dark:bg-neutral-950 shadow-xl transition-transform duration-300 ease-in-out ${
              sidebarOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            {/* Header — outside the scroll container so it never scrolls away */}
            <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-neutral-950">
              <span className="font-semibold text-sm">Categories</span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition"
                aria-label="Close sidebar"
              >
                <LuX className="h-4 w-4" />
              </button>
            </div>
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              <Sidebar
                active={activeCategory}
                channels={sidebarChannels}
                stats={sidebarStats}
              />
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_14rem] lg:grid-cols-[18rem_minmax(0,1fr)_17.5rem] xl:grid-cols-[20rem_minmax(0,1fr)_17.5rem]">
          {/* LEFT rail – desktop only, drawer on mobile */}
          <aside className="hidden lg:block lg:sticky lg:top-16 self-start">
            <Sidebar
              active={activeCategory}
              channels={sidebarChannels}
              stats={sidebarStats}
            />
          </aside>

          {/* CENTER feed */}
          <section className="space-y-4 min-w-0 mt-4 lg:mt-8">
            {/* #238 — search threads by title/body; combines with the category filter */}
            {view === "all" && (
              <div className="relative">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setShowChannelSuggestions(false);
                    setCurrentPage(1);
                    loadThreads("all", activeCategory, 1, searchTerm);
                  }}
                  className="flex items-center gap-2"
                >
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowChannelSuggestions(true);
                    }}
                    onFocus={() => setShowChannelSuggestions(true)}
                    onBlur={() =>
                      setTimeout(() => setShowChannelSuggestions(false), 120)
                    }
                    placeholder="Search threads…"
                    aria-label="Search threads"
                    role="combobox"
                    aria-expanded={
                      showChannelSuggestions && channelSuggestions.length > 0
                    }
                    aria-controls="forum-channel-suggestions"
                    aria-autocomplete="list"
                    autoComplete="off"
                    className="flex-1 min-w-0 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 px-4 py-2.5 text-sm outline-none focus:border-gray-400 dark:focus:border-white/25"
                  />
                  <button
                    type="submit"
                    className="shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-900 dark:bg-white/90 text-white dark:text-black hover:opacity-90 transition"
                  >
                    Search
                  </button>
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                        setShowChannelSuggestions(false);
                        setCurrentPage(1);
                        loadThreads("all", activeCategory, 1, "");
                      }}
                      className="shrink-0 px-3 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-white/10 hover:bg-gray-200 dark:hover:bg-white/10 transition"
                    >
                      Clear
                    </button>
                  )}
                </form>

                {/* Real-time channel match dropdown */}
                {showChannelSuggestions && channelSuggestions.length > 0 && (
                  <div
                    id="forum-channel-suggestions"
                    role="listbox"
                    className="absolute z-20 top-full left-0 right-0 mt-1.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-neutral-900 shadow-lg overflow-hidden"
                  >
                    <div className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide opacity-50">
                      Jump to channel
                    </div>
                    {channelSuggestions.map((cat) => {
                      const Icon = iconMap[cat.value] ?? LuMessagesSquare;
                      return (
                        <button
                          key={cat.value}
                          type="button"
                          role="option"
                          aria-selected={activeCategory === cat.value}
                          onMouseDown={(e) => {
                            // Fire before the input's onBlur so the dropdown
                            // doesn't close before the click registers.
                            e.preventDefault();
                            selectChannelSuggestion(cat.value);
                          }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-white/10 transition"
                        >
                          <Icon className="h-4 w-4 opacity-60 shrink-0" />
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {/* New thread composer */}
            <div className="rounded-2xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-4 py-3">
              <div className="flex items-center gap-3">
                <span
                  className="flex-1 text-gray-400 dark:text-white/60 select-none"
                  aria-hidden="true"
                >
                  Add a new thread
                </span>
                <button
                  onClick={handlePlusClick}
                  className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-300 dark:border-white/15 hover:bg-gray-200 dark:hover:bg-white/10 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Create thread"
                  title="Create thread"
                  disabled={creatingThread}
                >
                  {creatingThread ? (
                    <div className="h-4 w-4 border-2 border-gray-300 dark:border-white/30 border-t-black dark:border-t-white rounded-full animate-spin" />
                  ) : (
                    <FiPlus className="h-5 w-5" />
                  )}
                </button>
              </div>
              {loadingFeed && (
                <div className="mt-2 text-xs opacity-60">Refreshing…</div>
              )}
            </div>

            {/* Cards */}
            <div className="space-y-4">
              {feedError && !loadingFeed && (
                <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 p-6 text-center">
                  <p className="text-sm opacity-70">
                    Couldn&apos;t load the feed. Check your connection and try
                    again.
                  </p>
                  <button
                    onClick={() =>
                      loadThreads(view, activeCategory, currentPage)
                    }
                    className="mt-3 rounded-lg border border-gray-300 dark:border-white/20 px-4 py-1.5 text-sm hover:bg-gray-200 dark:hover:bg-white/10"
                  >
                    Retry
                  </button>
                </div>
              )}
              {!feedError && !loadingFeed && threads.length === 0 && (
                <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 p-6 text-center text-sm opacity-70">
                  {view === "saved"
                    ? "Nothing saved yet."
                    : view === "mine"
                      ? "You haven't posted anything yet."
                      : "No threads here yet. Be the first to post."}
                </div>
              )}
              {threads.map((t) => {
                const commentsOpen = openComments.has(t.id);

                // Inline post editor
                if (editingThreadId === t.id) {
                  return (
                    <div
                      key={t.id}
                      className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 p-4 space-y-3"
                    >
                      <div className="text-sm opacity-70">Editing thread</div>
                      <input
                        type="text"
                        value={threadEditTitle}
                        onChange={(e) => setThreadEditTitle(e.target.value)}
                        className="w-full rounded-lg bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 px-3 py-2 outline-none placeholder-gray-400 dark:placeholder-white/50"
                        placeholder="Thread title (required, 6-120 characters)"
                        maxLength={120}
                      />
                      <textarea
                        value={threadEditPost}
                        onChange={(e) => setThreadEditPost(e.target.value)}
                        onFocus={(e) => {
                          setTimeout(
                            () =>
                              e.target.scrollIntoView({
                                behavior: "smooth",
                                block: "nearest",
                              }),
                            300,
                          );
                        }}
                        className="w-full min-h-28 rounded-lg bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 px-3 py-2 outline-none placeholder-gray-400 dark:placeholder-white/50 resize-y"
                        placeholder="Thread body (optional)"
                        maxLength={5000}
                      />
                      <select
                        value={threadEditCategory}
                        onChange={(e) => setThreadEditCategory(e.target.value)}
                        className="w-full rounded-lg bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 px-3 py-2 outline-none"
                      >
                        {CATEGORIES.filter((cat) => cat.value !== "all").map(
                          (cat) => (
                            <option
                              key={cat.value}
                              value={cat.value}
                              className="bg-white dark:bg-neutral-900"
                            >
                              {cat.label}
                            </option>
                          ),
                        )}
                      </select>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={cancelThreadEdit}
                          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={updatingThread}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={saveThreadEdit}
                          className="px-3 py-2 rounded-lg bg-gray-900 dark:bg-white/90 text-white dark:text-black hover:bg-black dark:hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          disabled={
                            updatingThread || threadEditTitle.trim().length < 6
                          }
                        >
                          {updatingThread && (
                            <div className="h-4 w-4 border-2 border-white/30 dark:border-black/30 border-t-white dark:border-t-black rounded-full animate-spin" />
                          )}
                          Save changes
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={t.id} className="space-y-2.5">
                    <ThreadCard
                      id={t.id}
                      title={
                        t.title || t.post.substring(0, 60) || "Untitled Thread"
                      }
                      body={t.post}
                      author={{
                        name:
                          t.seededAuthorName?.trim() ||
                          t.author?.name ||
                          "Unknown User",
                        postCount: t.author?.postCount,
                      }}
                      createdAt={t.createdAt}
                      bookmarked={t.userSaved}
                      canEdit={t.userId === userId}
                      status={t.status || "APPROVED"}
                      rejectionReason={t.rejectionReason}
                      isOwner={t.userId === userId}
                      userVote={t.userVote}
                      upvoteCount={t.upvoteCount}
                      onReply={() => openReplyInline(t.id)}
                      onEdit={() => startThreadEdit(t.id)}
                      onDelete={() => deleteThread(t.id)}
                      onBookmarkToggle={(id, next) => toggleSave(id, next)}
                      onUpvote={() => voteThread(t.id, 1)}
                      onDownvote={() => voteThread(t.id, -1)}
                      onReport={() =>
                        setReportTarget({ type: "THREAD", targetId: t.id })
                      }
                      isDeleting={deletingThread === t.id}
                      isTogglingUpvote={votingThread?.id === t.id && votingThread?.value === 1}
                      isTogglingDownvote={votingThread?.id === t.id && votingThread?.value === -1}
                      isTogglingSave={togglingSave === t.id}
                    />

                    {/* View responses button */}
                    <div className="flex items-center gap-3 px-1">
                      <button
                        className="text-sm opacity-70 hover:opacity-100 underline-offset-2 hover:underline"
                        onClick={() => toggleCommentsUI(t.id)}
                      >
                        {commentsOpen
                          ? "Hide responses"
                          : `View responses (${t.commentCount})`}
                      </button>
                    </div>

                    {/* Inline reply composer */}
                    {replyOpenId === t.id && (
                      <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 p-3">
                        <label className="block text-sm opacity-70 mb-1">
                          Add a response
                        </label>
                        <textarea
                          value={replyDrafts[t.id] || ""}
                          onChange={(e) =>
                            setReplyDrafts((d) => ({
                              ...d,
                              [t.id]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                              e.preventDefault();
                              submitReplyInline(t.id);
                            }
                          }}
                          onFocus={(e) => {
                            // Scroll into view on mobile when keyboard opens
                            setTimeout(
                              () =>
                                e.target.scrollIntoView({
                                  behavior: "smooth",
                                  block: "nearest",
                                }),
                              300,
                            );
                          }}
                          placeholder="Write your reply…"
                          className="w-full min-h-24 rounded-lg bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 px-3 py-2 outline-none placeholder-gray-400 dark:placeholder-white/50 resize-y"
                        />
                        <div className="mt-2 flex items-center justify-end gap-2">
                          <button
                            onClick={() => setReplyOpenId(null)}
                            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={addingComment === t.id}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => submitReplyInline(t.id)}
                            className="px-3 py-2 rounded-lg bg-gray-900 dark:bg-white/90 text-white dark:text-black hover:bg-black dark:hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            title="Ctrl/⌘ + Enter"
                            disabled={addingComment === t.id}
                          >
                            {addingComment === t.id && (
                              <div className="h-4 w-4 border-2 border-white/30 dark:border-black/30 border-t-white dark:border-t-black rounded-full animate-spin" />
                            )}
                            Post reply
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Comments */}
                    {commentsOpen && t.comments.length > 0 && (
                      <div className="space-y-3">
                        {t.comments.map((c, idx) => {
                          // Check if this comment is being edited
                          if (editingCommentId === c.id) {
                            return (
                              <div
                                key={c.id}
                                className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 px-3 py-3"
                              >
                                <div className="text-sm opacity-70 mb-2">
                                  Editing comment
                                </div>
                                <textarea
                                  value={commentEditText}
                                  onChange={(e) =>
                                    setCommentEditText(e.target.value)
                                  }
                                  onFocus={(e) => {
                                    setTimeout(
                                      () =>
                                        e.target.scrollIntoView({
                                          behavior: "smooth",
                                          block: "nearest",
                                        }),
                                      300,
                                    );
                                  }}
                                  className="w-full min-h-20 rounded-lg bg-white dark:bg-white/5 border border-gray-300 dark:border-white/10 px-3 py-2 outline-none placeholder-gray-400 dark:placeholder-white/50 resize-y"
                                  placeholder="Comment content"
                                />
                                <div className="mt-2 flex items-center justify-end gap-2">
                                  <button
                                    onClick={cancelCommentEdit}
                                    className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 hover:bg-gray-200 dark:hover:bg-white/10 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={updatingComment}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => saveCommentEdit(t.id)}
                                    className="px-3 py-2 rounded-lg bg-gray-900 dark:bg-white/90 text-white dark:text-black hover:bg-black dark:hover:bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    disabled={updatingComment}
                                  >
                                    {updatingComment && (
                                      <div className="h-4 w-4 border-2 border-white/30 dark:border-black/30 border-t-white dark:border-t-black rounded-full animate-spin" />
                                    )}
                                    Save
                                  </button>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div
                              key={c.id}
                              className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 px-3 py-3"
                            >
                              <div className="flex items-start gap-3">
                                {/* User Avatar */}
                                <div className="shrink-0">
                                  {c.user_image ? (
                                    <Image
                                      src={c.user_image}
                                      alt={c.user_name}
                                      width={40}
                                      height={40}
                                      className="w-10 h-10 rounded-full object-cover ring-1 ring-gray-200 dark:ring-white/10"
                                    />
                                  ) : (
                                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-white/10 flex items-center justify-center font-semibold text-sm ring-1 ring-gray-300 dark:ring-white/10">
                                      {c.user_name
                                        ? c.user_name
                                            .split(" ")
                                            .map((n) => n[0])
                                            .join("")
                                            .toUpperCase()
                                            .substring(0, 2)
                                        : "??"}
                                    </div>
                                  )}
                                </div>

                                {/* Comment Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-sm">
                                      {c.user_name || "Unknown User"}
                                    </span>
                                    <span className="text-xs opacity-50">
                                      {new Date(
                                        c.createdAt,
                                      ).toLocaleDateString()}
                                    </span>
                                    {/* Comment status badges */}
                                    {String(
                                      c.status || "approved",
                                    ).toLowerCase() === "pending" && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-medium">
                                        <span className="h-1 w-1 rounded-full bg-amber-500 animate-pulse" />
                                        Pending
                                      </span>
                                    )}
                                    {String(
                                      c.status || "approved",
                                    ).toLowerCase() === "rejected" && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[10px] font-medium">
                                        Rejected
                                      </span>
                                    )}
                                  </div>
                                  {/* Rejection reason for comments */}
                                  {String(c.status || "").toLowerCase() ===
                                    "rejected" &&
                                    c.rejectionReason && (
                                      <p className="text-xs text-red-600 dark:text-red-400 mb-1">
                                        Reason: {c.rejectionReason}
                                      </p>
                                    )}
                                  <div className="opacity-90 text-[15px] leading-relaxed wrap-break-word">
                                    {c.comment}
                                  </div>

                                  {/* Edit/Delete buttons - only show for user's own comments */}
                                  {c.user_id === userId && (
                                    <div className="mt-2 flex items-center gap-2">
                                      <button
                                        onClick={() =>
                                          startCommentEdit(c.id, c.comment)
                                        }
                                        className="text-xs opacity-60 hover:opacity-100 underline-offset-2 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={
                                          deletingComment === c.id ||
                                          updatingComment
                                        }
                                      >
                                        Edit
                                      </button>
                                      <span className="opacity-30">•</span>
                                      <button
                                        onClick={() =>
                                          deleteComment(t.id, c.id)
                                        }
                                        className="text-xs text-red-500 dark:text-red-300 opacity-60 hover:opacity-100 underline-offset-2 hover:underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                        disabled={
                                          deletingComment === c.id ||
                                          updatingComment
                                        }
                                      >
                                        {deletingComment === c.id && (
                                          <div className="h-3 w-3 border-2 border-red-500/30 dark:border-red-300/30 border-t-red-500 dark:border-t-red-300 rounded-full animate-spin" />
                                        )}
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                  {/* Report button for other users' approved comments */}
                                  {c.user_id !== userId &&
                                    String(
                                      c.status || "approved",
                                    ).toLowerCase() === "approved" && (
                                      <div className="mt-2">
                                        <button
                                          onClick={() =>
                                            setReportTarget({
                                              type: "COMMENT",
                                              targetId: c.id,
                                              postId: t.id,
                                            })
                                          }
                                          className="text-xs opacity-50 hover:opacity-100 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1 transition"
                                        >
                                          <FiFlag className="h-3 w-3" />
                                          Report
                                        </button>
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 py-6">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1 || loadingFeed}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 text-sm hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => {
                      // Show first, last, current ±1, and ellipsis placeholders
                      return (
                        p === 1 ||
                        p === totalPages ||
                        Math.abs(p - currentPage) <= 1
                      );
                    })
                    .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1) {
                        acc.push("…");
                      }
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === "…" ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-2 text-gray-400 dark:text-white/40 text-sm select-none"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          onClick={() => goToPage(item as number)}
                          disabled={loadingFeed}
                          className={`min-w-9 h-9 rounded-lg text-sm font-medium transition disabled:cursor-not-allowed ${
                            currentPage === item
                              ? "bg-gray-900 dark:bg-white/90 text-white dark:text-black"
                              : "border border-gray-300 dark:border-white/15 hover:bg-gray-100 dark:hover:bg-white/10"
                          }`}
                        >
                          {item}
                        </button>
                      ),
                    )}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages || loadingFeed}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 text-sm hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            )}
          </section>

          {/* RIGHT rail – hidden on mobile, shown on tablet+ */}
          <aside className="hidden md:block md:sticky md:top-16 self-start mt-8">
            <div className="rounded-2xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-4">
              <nav className="space-y-3">
                <button
                  className={`w-full text-left px-3 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 ${
                    view === "all" ? "bg-gray-200 dark:bg-white/10" : ""
                  }`}
                  onClick={() => setView("all")}
                  aria-current={view === "all" ? "page" : undefined}
                >
                  Home
                </button>

                <button
                  className={`w-full text-left px-3 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 ${
                    view === "mine" ? "bg-gray-200 dark:bg-white/10" : ""
                  }`}
                  onClick={() => setView("mine")}
                  aria-current={view === "mine" ? "page" : undefined}
                >
                  Your Threads
                </button>

                <button
                  className={`w-full text-left px-3 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 flex items-center justify-between ${
                    view === "saved" ? "bg-gray-200 dark:bg-white/10" : ""
                  }`}
                  onClick={() => setView("saved")}
                  aria-current={view === "saved" ? "page" : undefined}
                >
                  <span>Saved</span>
                  {savedCount > 0 && (
                    <span className="ml-3 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded bg-gray-300 dark:bg-white/20 text-xs">
                      {savedCount}
                    </span>
                  )}
                </button>
              </nav>
            </div>
          </aside>
        </div>
      </div>

      {/* Create Thread Modal */}
      <CreateThreadModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={createThreadWithModal}
        isSubmitting={creatingThread}
      />

      {/* Toast notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-4">
          <div
            className={`px-4 py-3 rounded-xl shadow-lg border text-sm font-medium ${
              toast.type === "success"
                ? "bg-green-50 dark:bg-green-900/40 border-green-200 dark:border-green-700 text-green-800 dark:text-green-200"
                : toast.type === "error"
                  ? "bg-red-50 dark:bg-red-900/40 border-red-200 dark:border-red-700 text-red-800 dark:text-red-200"
                  : "bg-amber-50 dark:bg-amber-900/40 border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200"
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}

      {/* Report modal */}
      {reportTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md mx-4 bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-white/10 shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-semibold">
              Report {reportTarget.type === "THREAD" ? "Thread" : "Comment"}
            </h3>
            <p className="text-sm text-gray-600 dark:text-white/60">
              Why are you reporting this content? (optional)
            </p>
            <textarea
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Describe the issue..."
              className="w-full min-h-24 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-300 dark:border-white/10 px-3 py-2 outline-none placeholder-gray-400 dark:placeholder-white/50 resize-y text-sm"
              maxLength={500}
            />
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setReportTarget(null);
                  setReportReason("");
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-white/15 hover:bg-gray-100 dark:hover:bg-white/10 text-sm"
                disabled={submittingReport}
              >
                Cancel
              </button>
              <button
                onClick={submitReport}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={submittingReport}
              >
                {submittingReport && (
                  <div className="h-3 w-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Forum() {
  return (
    <Suspense
      fallback={
        <div className="min-h-dvh pt-16 flex items-center justify-center">
          <p>Loading...</p>
        </div>
      }
    >
      <ForumContent />
    </Suspense>
  );
}
